import { useSyncExternalStore, type ReactNode } from 'react'
import { Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/Card'
import {
  getSupportSessionStatus,
  subscribeSupportSession,
  type SupportSessionStatus,
} from '@/lib/supportSession'

type ClosedStatus = Extract<SupportSessionStatus, 'expired' | 'ended' | 'invalid_link'>

function isClosed(status: SupportSessionStatus): status is ClosedStatus {
  return status === 'expired' || status === 'ended' || status === 'invalid_link'
}

/**
 * EP-160 section 2.3: once a support tab's session expired (any 401), was
 * ended, or its link was refused, the whole app is replaced by this screen.
 * A support tab never goes to /login. Ordinary tabs render children as is.
 */
export function SupportSessionGate({ children }: { children: ReactNode }) {
  const status = useSyncExternalStore(subscribeSupportSession, getSupportSessionStatus)
  if (!isClosed(status)) return <>{children}</>
  return <SupportSessionClosed status={status} />
}

function SupportSessionClosed({ status }: { status: ClosedStatus }) {
  const { t } = useTranslation('common')
  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
      <Card
        className="shadow-xl max-w-md w-full"
        bodyClassName="items-center text-center gap-4"
        role="alert"
        data-testid="support-session-closed"
      >
        <div className="w-16 h-16 rounded-full bg-warning/10 flex items-center justify-center">
          <Clock className="w-8 h-8 text-warning" aria-hidden="true" />
        </div>
        <h1 className="card-title">{t(`support_session.${status}_title`)}</h1>
        <p className="text-base-content/70">{t(`support_session.${status}_body`)}</p>
      </Card>
    </div>
  )
}
